import React from "react";
import { isEmpty } from "lodash";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";

import { CloseButton, OnlineLink, Button, Mid } from "components";
import { DATE_FORMAT } from "containers/terminalSolution/constants";
import {
	getFormattedDate,
	getFullName,
	hasPermissions,
	permissionList,
} from "helpers";
import { useBackendPlatform } from "hooks";
import {
	BackEndPlatformTypes,
	FrontEndPlatformTypes,
	TerminalFileBuildOptionTypes,
	TerminalInstallTypes,
	TerminalProductNameTypes,
	TerminalSolutionOrigin,
	TerminalSolutionTypes,
	TerminalStatusTypes,
} from "types/enums";

import styles from "./modal.module.scss";

type PropsType = {
	show: boolean;
	onHide: () => void;
	terminalData: any;
	merchantGeneralData: any;
};

const LabeledValue = ({ label, value }) => (
	<div className="d-flex mt-3">
		<div className={styles.label}>{label}:</div>
		<div className={styles.value}>{value}</div>
	</div>
);

const TerminalPreviewModal = ({
	show,
	onHide,
	terminalData,
	merchantGeneralData,
}: PropsType) => {
	const {
		id: terminalSolutionId,
		number,
		status,
		updatedAt,
		updatedBy,
		terminalProgramType,
		tidVNumber,
		backEndPlatform,
		frontEndMid,
		installation,
		terminal,
		shipping,
		gateway,
		pointOfSale,
		fileBuild,
		origin,
		isTerminal,
		isPointOfSale,
		isGateway,
	} = terminalData;

	const { dbaName, id: merchantId } = merchantGeneralData;

	const { isBackEndPlatformOmaha, isBackEndPlatformNorth } =
		useBackendPlatform(backEndPlatform);

	const getFrontEndPlatformLabel = () => {
		const {
			northFrontEndPlatform,
			omahaFrontEndPlatform,
			nashvilleFrontEndPlatform,
			otherFrontEndPlatform,
			buypassFrontEndPlatform,
			compassFrontEndPlatform,
			otherFrontEndPlatformName,
		} = terminalData;

		const messages = [
			northFrontEndPlatform
				? FrontEndPlatformTypes.North.displayName
				: null,
			omahaFrontEndPlatform
				? FrontEndPlatformTypes.Omaha.displayName
				: null,
			nashvilleFrontEndPlatform
				? FrontEndPlatformTypes.Nashville.displayName
				: null,
			otherFrontEndPlatform ? otherFrontEndPlatformName : null,
			buypassFrontEndPlatform
				? FrontEndPlatformTypes.BuyPass.displayName
				: null,
			compassFrontEndPlatform
				? FrontEndPlatformTypes.Compass.displayName
				: null,
		].filter(Boolean);

		return messages.join(", ");
	};

	const getTerminalSolutionTypeLabel = () => {
		const { isFileBuildOnly, isGateway, isPointOfSale, isTerminal } =
			terminalData;

		if (isFileBuildOnly) {
			return TerminalSolutionTypes.FileBuildOnly.displayName;
		}

		if (isGateway) {
			return TerminalSolutionTypes.Gateway.displayName;
		}

		if (isPointOfSale) {
			return TerminalSolutionTypes.PointOfSale.displayName;
		}

		if (isTerminal) {
			return TerminalSolutionTypes.Terminal.displayName;
		}

		return "";
	};

	const getSerialsTerminalDevices = (terminalDevices: [any]) => {
		return terminalDevices.reduce(
			(previousValue, currentValue) =>
				previousValue
					? `${previousValue}/${currentValue?.serialNumber}`
					: currentValue?.serialNumber,
			"",
		);
	};

	const isEditTerminalSolution = hasPermissions(
		permissionList.AddTerminalManually,
	);

	return (
		<Modal
			show={show}
			onHide={onHide}
			animation={false}
			centered
			className={styles.centeredLabelsModal}
		>
			<Modal.Header className="pb-0">
				<CloseButton onClick={onHide} />
			</Modal.Header>

			<Modal.Body className="d-flex flex-column pt-0">
				<LabeledValue
					label="Terminal Solution Type"
					value={getTerminalSolutionTypeLabel()}
				/>
				<LabeledValue
					label="MID"
					value={
						<Link
							to={`/merchant/list/info/${merchantId}/`}
							target="_blank"
						>
							<Mid merchant={merchantGeneralData} />
						</Link>
					}
				/>
				<LabeledValue
					label="Doing Business As Name (DBA)"
					value={
						<Link
							to={`/merchant/list/info/${merchantId}/`}
							target="_blank"
						>
							{dbaName}
						</Link>
					}
				/>
				<LabeledValue label="Terminal Solution #" value={number} />
				<LabeledValue
					label="Status of Terminal Solution"
					value={TerminalStatusTypes.getByName(status)?.displayName}
				/>
				<LabeledValue
					label="Complete/ Updated Date"
					value={getFormattedDate({
						date: updatedAt,
						format: DATE_FORMAT,
					})}
				/>
				<LabeledValue
					label="Complete/ Updated By"
					value={getFullName(updatedBy)}
				/>
				<LabeledValue
					label="Origin"
					value={
						TerminalSolutionOrigin.getByName(origin)?.displayName
					}
				/>
				<LabeledValue
					label="Program"
					value={terminalProgramType?.displayName}
				/>
				<LabeledValue label="TID / V #" value={tidVNumber} />
				<LabeledValue
					label="Back End Platform"
					value={
						BackEndPlatformTypes.getByName(backEndPlatform)
							?.displayName
					}
				/>
				{isBackEndPlatformNorth ||
					(isBackEndPlatformOmaha && (
						<>
							<LabeledValue
								label="Front End Platform"
								value={getFrontEndPlatformLabel()}
							/>

							<LabeledValue
								label="Front End Platform MID"
								value={frontEndMid}
							/>
						</>
					))}
				<LabeledValue
					label="Install (Aurora/ Agent)"
					value={
						TerminalInstallTypes.getByValue(
							Number(installation?.terminalInstall),
						)?.displayName
					}
				/>
				<LabeledValue
					label="Install Date"
					value={installation?.installAt}
				/>
				<LabeledValue
					label="Uninstall date"
					value={installation?.uninstallAt}
				/>
				{isTerminal && (
					<>
						<LabeledValue
							label="Ownership"
							value={terminal.terminalType.displayName}
						/>

						<LabeledValue
							label="Manufacturer"
							value={terminal?.manufacturer?.name}
						/>

						<LabeledValue
							label="Model"
							value={terminal?.model?.name}
						/>

						<LabeledValue
							label="Serial #"
							value={getSerialsTerminalDevices(
								terminal?.terminalDevices,
							)}
						/>

						<LabeledValue label="Vendor" value={shipping?.vendor} />

						<LabeledValue
							label="Tracking Number"
							value={shipping?.trackingNumber}
						/>
					</>
				)}
				{isGateway && (
					<>
						<LabeledValue
							label="Gateway Name"
							value={gateway?.gatewayName?.name}
						/>

						<LabeledValue
							label="Gateway ID"
							value={gateway?.gatewayId}
						/>
					</>
				)}
				{isPointOfSale && (
					<>
						<LabeledValue
							label="POS/ Software Product Name"
							value={
								isEmpty(pointOfSale?.nameIfOther)
									? TerminalProductNameTypes.getByName(
											pointOfSale?.product?.name,
									  )?.displayName
									: pointOfSale?.nameIfOther
							}
						/>
					</>
				)}
				<LabeledValue
					label="File Build"
					value={
						TerminalFileBuildOptionTypes.getByValue(
							fileBuild?.fileBuildOption,
						)?.displayName
					}
				/>
			</Modal.Body>
			{isEditTerminalSolution && (
				<Modal.Footer className="d-flex justify-content-center">
					<Button
						value="Edit"
						as={OnlineLink}
						to={`/merchant/list/info/${merchantId}/terminal-solutions/${terminalSolutionId}/edit`}
						onClick={onHide}
					/>
				</Modal.Footer>
			)}
		</Modal>
	);
};

export default TerminalPreviewModal;
